import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useGetSuperadminGym } from "@workspace/api-client-react";
import { formatCurrency } from "@/lib/utils";
import { Building2, Users, Calendar, DollarSign, ArrowLeft, Shield, Ban } from "lucide-react";
import { useRoute, useLocation } from "wouter";
import { apiFetch } from "@/lib/api";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export default function SuperadminGymDetail() {
  const [, params] = useRoute("/superadmin/gym/:id");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const gymId = params?.id || "";
  const { data: gymData, isLoading, refetch } = useGetSuperadminGym(gymId, { query: { enabled: !!gymId } });
  const gym = gymData as any;
  const [actionLoading, setActionLoading] = useState(false);

  async function toggleStatus(activate: boolean) {
    setActionLoading(true);
    try {
      await apiFetch(`/superadmin/gyms/${gymId}/activate`, { method: "PUT", body: JSON.stringify({ isActive: activate }) });
      toast({ title: activate ? "Gym activated" : "Gym suspended" });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-32 bg-white/5 rounded-xl animate-pulse" />)}</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => setLocation("/superadmin/gyms")} className="text-muted-foreground hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">{gym?.name || "Gym"}</h1>
            <p className="text-muted-foreground">/{gym?.slug} · {gym?.city || "Unknown location"}</p>
            <div className="flex items-center gap-3 mt-3">
              <span className={cn(
                "text-xs px-2 py-0.5 rounded-full font-semibold uppercase",
                gym?.plan === "PREMIUM" ? "bg-primary/20 text-primary" : "bg-white/10 text-muted-foreground"
              )}>
                {gym?.plan || "FREE"}
              </span>
              <span className={cn(
                "text-xs px-2 py-0.5 rounded-full font-semibold uppercase",
                gym?.isActive !== false ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"
              )}>
                {gym?.isActive !== false ? "ACTIVE" : "SUSPENDED"}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            {gym?.isActive !== false ? (
              <Button size="sm" variant="ghost" disabled={actionLoading} onClick={() => toggleStatus(false)} className="text-red-400 hover:bg-red-500/10">
                <Ban className="w-4 h-4 mr-1" /> Suspend
              </Button>
            ) : (
              <Button size="sm" disabled={actionLoading} onClick={() => toggleStatus(true)} className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
                <Shield className="w-4 h-4 mr-1" /> Activate
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Revenue</CardDescription>
              <CardTitle className="text-3xl font-mono text-primary">{formatCurrency(gym?.totalRevenue || 0)}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Sessions</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{gym?.totalSessions || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Trainers</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{gym?.trainerCount || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Students</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{gym?.studentCount || 0}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {gym?.trainers && gym.trainers.length > 0 && (
          <Card className="glass-panel border-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Trainers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {gym.trainers.map((t: any) => (
                  <div key={t.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/5 border border-white/5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                        {t.firstName?.[0]}{t.lastName?.[0]}
                      </div>
                      <div>
                        <p className="font-bold text-white">{t.firstName} {t.lastName}</p>
                        <p className="text-xs text-muted-foreground">{t.email}</p>
                      </div>
                    </div>
                    <span className="text-sm font-mono text-muted-foreground">{formatCurrency(t.hourlyRate || 0)}/hr</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
